import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";
import { api, authFetch } from "../utils/api";
import { compressImage } from "../utils/imageCompress";
import { VERIFICATION_DOC_TYPES } from "../utils/verificationDocs";
import UiIcon from "../components/UiIcon";
import VerificationTracker from "../components/VerificationTracker";
import { Screen, PageHeader, Card, SectionTitle, Button, Chip, Field, fieldStyle, K } from "../components/UiKit";

// Vérification du compte — même process qu'avant (demande examinée à la main par l'équipe, qui
// débloque toute l'app côté serveur, voir middleware/auth.ts), nouveau visuel et nouvelles règles :
//  - le SELFIE est seul obligatoire (l'ancienne pièce d'identité obligatoire est supprimée) ;
//  - une pièce justificative (identité, passeport, facture, titre de transport…) est FACULTATIVE
//    et peut s'ajouter plus tard, ici même depuis les Réglages ;
//  - une fois la demande envoyée, on choisit le nom affiché (prénom seul, pseudo, nom complet).
// Miroir de OLTODO/app/verify-identity.tsx.

type UploadKind = "verification-selfie" | "verification";

function useUploader(lang: string) {
  const [uploading, setUploading] = useState(false);
  const upload = async (file: File, type: UploadKind): Promise<string> => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", await compressImage(file, 2000, 0.9));
      const res = await authFetch(`/uploads/${type}`, { method: "POST", body: fd });
      const data = await res.json();
      if (data?.url) return data.url;
      alert(t(lang, "error_upload_document"));
    } catch {
      alert(t(lang, "error_upload_document"));
    } finally {
      setUploading(false);
    }
    return "";
  };
  return { uploading, upload };
}

// Choix de la nature de la pièce (grille de tuiles) + envoi de la photo — partagé entre le
// formulaire initial et l'ajout ultérieur.
function DocPicker({ lang, docType, setDocType, docUrl, setDocUrl }: { lang: string; docType: string; setDocType: (k: string) => void; docUrl: string; setDocUrl: (u: string) => void }) {
  const { uploading, upload } = useUploader(lang);
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 8, marginBottom: 12 }}>
        {VERIFICATION_DOC_TYPES.map(d => {
          const active = docType === d.key;
          return (
            <button key={d.key} onClick={() => setDocType(active ? "" : d.key)} style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, minHeight: 48, padding: "8px 12px", borderRadius: 12, border: `1.5px solid ${active ? K.ink : K.line}`, backgroundColor: active ? K.ink : K.white, color: active ? K.white : K.ink, fontSize: 13, fontWeight: 600, textAlign: "left", lineHeight: 1.2 }}>
              <UiIcon name={d.icon} size={18} />
              <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{t(lang, d.i18nKey)}</span>
            </button>
          );
        })}
      </div>
      {docType && (
        <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, height: docUrl ? 150 : 56, borderRadius: 12, border: `1.5px dashed ${docUrl ? K.ink : "#CFCFCF"}`, backgroundColor: K.surface, cursor: "pointer", overflow: "hidden", color: K.muted, fontSize: 13.5, fontWeight: 600 }}>
          <input type="file" accept="image/*" style={{ display: "none" }} onChange={async e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) { const u = await upload(f, "verification"); if (u) setDocUrl(u); } }} />
          {docUrl ? <img src={docUrl} alt="" style={{ height: "100%", width: "100%", objectFit: "cover" }} /> : (
            <><UiIcon name="upload" size={18} />{uploading ? t(lang, "uploading_ellipsis") : t(lang, "vfy_doc_choose")}</>
          )}
        </label>
      )}
    </>
  );
}

// Nom affiché : prénom seul (conseillé), pseudo, ou prénom + nom. users.name porte le nom affiché
// partout dans l'app ; le nom complet reste connu de l'équipe pour valider le compte.
function DisplayNameCard({ lang }: { lang: string }) {
  const { user, setUser } = useApp();
  const [me, setMe] = useState<any>(null);
  const [mode, setMode] = useState<"first" | "pseudo" | "full">("full");
  const [pseudo, setPseudo] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/users/me").then(d => {
      if (!d?.user) return;
      setMe(d.user);
      setMode(d.user.display_name_mode || "full");
      setPseudo(d.user.pseudo || "");
    }).catch(() => {});
  }, []);

  const legal = String(me?.legal_name || me?.name || "").trim();
  const first = legal.split(/\s+/)[0] || "";
  const options: { key: "first" | "pseudo" | "full"; icon: any; title: string; hint: string; preview: string; recommended?: boolean }[] = [
    { key: "first", icon: "user", title: t(lang, "vfy_name_first"), hint: t(lang, "vfy_name_first_hint"), preview: first, recommended: true },
    { key: "pseudo", icon: "pencil", title: t(lang, "vfy_name_pseudo"), hint: t(lang, "vfy_name_pseudo_hint"), preview: pseudo || "…" },
    { key: "full", icon: "users", title: t(lang, "vfy_name_full"), hint: t(lang, "vfy_name_full_hint"), preview: legal },
  ];

  const save = async () => {
    if (saving) return;
    setSaving(true); setErr(""); setSaved(false);
    const data = await api.put("/users/me/display-name", { mode, pseudo: mode === "pseudo" ? pseudo.trim() : undefined }).catch(() => null);
    if (data?.name) {
      setSaved(true);
      if (user) setUser({ ...user, name: data.name });
    } else {
      setErr(data?.error || t(lang, "error_connection"));
    }
    setSaving(false);
  };

  if (!me) return null;
  return (
    <Card>
      <SectionTitle hint={t(lang, "vfy_name_sub")}>{t(lang, "vfy_name_title")}</SectionTitle>
      {options.map(o => {
        const active = mode === o.key;
        return (
          <div key={o.key} onClick={() => { setMode(o.key); setSaved(false); }} style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, marginBottom: 8, borderRadius: 14, border: `1.5px solid ${active ? K.ink : K.line}`, cursor: "pointer", minWidth: 0 }}>
            <div style={{ width: 38, height: 38, borderRadius: 19, backgroundColor: active ? K.ink : K.surface, color: active ? K.white : K.ink, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <UiIcon name={o.icon} size={19} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 14.5, fontWeight: 700 }}>{o.title}</span>
                {o.recommended && <Chip ink>{t(lang, "vfy_name_recommended")}</Chip>}
              </div>
              <div style={{ fontSize: 12, color: K.muted, marginTop: 2, lineHeight: 1.4 }}>{o.hint}</div>
              <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4, overflowWrap: "anywhere" }}>{o.preview}</div>
            </div>
            <span style={{ width: 22, height: 22, borderRadius: 11, border: `2px solid ${active ? K.ink : "#CFCFCF"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {active && <span style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: K.ink }} />}
            </span>
          </div>
        );
      })}
      {mode === "pseudo" && (
        <Field label={t(lang, "vfy_name_pseudo")}>
          <input value={pseudo} onChange={e => { setPseudo(e.target.value.slice(0, 30)); setSaved(false); }} placeholder={t(lang, "vfy_name_pseudo_placeholder")} autoCapitalize="none" autoCorrect="off" style={fieldStyle} />
        </Field>
      )}
      {!!err && <div style={{ fontSize: 12.5, color: K.danger, marginBottom: 10 }}>{err}</div>}
      <Button onClick={save} disabled={saving || (mode === "pseudo" && pseudo.trim().length < 3)} icon={saved ? "check" : undefined}>{saved ? t(lang, "vfy_name_saved") : t(lang, "vfy_name_save")}</Button>
    </Card>
  );
}

export default function VerifyIdentity() {
  const navigate = useNavigate();
  const { lang } = useApp();
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [request, setRequest] = useState<any>(null);
  const [docRequest, setDocRequest] = useState<any>(null);
  const [selfieUrl, setSelfieUrl] = useState("");
  const [docUrl, setDocUrl] = useState("");
  const [docType, setDocType] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [addingDoc, setAddingDoc] = useState(false);
  const [trackerOpen, setTrackerOpen] = useState(false);
  const selfie = useUploader(lang);
  const { user } = useApp();

  useEffect(() => {
    api.get("/users/me/verification-request").then(d => { setRequest(d?.request || null); setDocRequest(d?.document_request || null); }).catch(() => {}).finally(() => setLoadingStatus(false));
  }, []);

  const submit = () => {
    if (!selfieUrl || submitting || (docUrl && !docType)) return;
    setSubmitting(true);
    api.post("/users/me/verification-request", { selfie_url: selfieUrl, document_url: docUrl || undefined, document_type: docUrl ? docType : undefined, note: note.trim() || undefined })
      .then(data => {
        if (data?.request) { setRequest(data.request); setSelfieUrl(""); setDocUrl(""); setDocType(""); setNote(""); }
        else alert(data?.error || t(lang, "error_connection"));
      })
      .catch(() => alert(t(lang, "error_connection")))
      .finally(() => setSubmitting(false));
  };

  const submitDocument = () => {
    if (!docUrl || !docType || addingDoc) return;
    setAddingDoc(true);
    api.post("/users/me/verification-request", { kind: "document", document_url: docUrl, document_type: docType })
      .then(data => {
        if (data?.request) { setDocRequest(data.request); setDocUrl(""); setDocType(""); }
        else alert(data?.error || t(lang, "error_connection"));
      })
      .catch(() => alert(t(lang, "error_connection")))
      .finally(() => setAddingDoc(false));
  };

  const status = request?.status;

  // Bloc "pièce justificative" (facultatif) réutilisé pour un compte déjà envoyé/vérifié.
  const docSection = (
    <Card>
      <SectionTitle hint={t(lang, "vfy_doc_later")}>{t(lang, "vfy_settings_add_doc")}</SectionTitle>
      {docRequest?.status === "pending" ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13.5, fontWeight: 600 }}><UiIcon name="clock" size={18} />{t(lang, "vfy_doc_pending")}</div>
      ) : (
        <>
          <DocPicker lang={lang} docType={docType} setDocType={setDocType} docUrl={docUrl} setDocUrl={setDocUrl} />
          {!!docUrl && !!docType && <div style={{ marginTop: 12 }}><Button onClick={submitDocument} disabled={addingDoc}>{addingDoc ? t(lang, "sending_ellipsis") : t(lang, "vfy_doc_send")}</Button></div>}
        </>
      )}
    </Card>
  );

  return (
    <Screen>
      <PageHeader title={t(lang, "verify_identity_title")} onBack={() => navigate(-1)} />

      {loadingStatus ? (
        <div style={{ textAlign: "center", color: K.faint, marginTop: 40 }}>{t(lang, "msg_loading")}</div>
      ) : status === "approved" ? (
        <>
          <Card style={{ textAlign: "center", padding: "28px 18px" }}>
            <div style={{ width: 64, height: 64, borderRadius: 32, backgroundColor: K.ink, color: K.white, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}><UiIcon name="shieldCheck" size={32} /></div>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 14 }}>{t(lang, "verify_identity_status_approved")}</div>
            <Button variant="soft" small onClick={() => setTrackerOpen(true)}>{t(lang, "vfy_track_title")}</Button>
          </Card>
          {docSection}
          <DisplayNameCard lang={lang} />
        </>
      ) : status === "pending" ? (
        <>
          <Card style={{ textAlign: "center", padding: "28px 18px" }}>
            <div style={{ width: 64, height: 64, borderRadius: 32, backgroundColor: K.surface, color: K.ink, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}><UiIcon name="clock" size={30} /></div>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>{t(lang, "vfy_sent_title")}</div>
            <div style={{ fontSize: 13.5, color: K.muted, lineHeight: 1.55, marginBottom: 14 }}>{t(lang, "vfy_sent_body")}</div>
            <Button variant="soft" small onClick={() => setTrackerOpen(true)}>{t(lang, "vfy_track_title")}</Button>
          </Card>
          <DisplayNameCard lang={lang} />
          {docSection}
        </>
      ) : (
        <>
          {status === "rejected" && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, backgroundColor: K.surface, borderRadius: 14, padding: "12px 14px", marginBottom: 14, fontSize: 13, fontWeight: 600, lineHeight: 1.4 }}>
              <UiIcon name="alert" size={18} />{t(lang, "verify_rejected_retry")}
            </div>
          )}
          <p style={{ fontSize: 14, color: K.muted, lineHeight: 1.55, marginBottom: 18 }}>{t(lang, "vfy_intro")}</p>

          <Card>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 16, fontWeight: 700 }}>{t(lang, "vfy_step_selfie_title")}</span>
              <Chip ink>{t(lang, "vfy_required")}</Chip>
            </div>
            <div style={{ fontSize: 12.5, color: K.muted, lineHeight: 1.45, marginBottom: 14 }}>{t(lang, "vfy_step_selfie_desc")}</div>
            <label style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, height: selfieUrl ? 220 : 140, borderRadius: 14, border: `1.5px dashed ${selfieUrl ? K.ink : "#CFCFCF"}`, backgroundColor: K.surface, cursor: "pointer", overflow: "hidden", color: K.muted, fontSize: 13.5, fontWeight: 600 }}>
              <input type="file" accept="image/*" capture="user" style={{ display: "none" }} onChange={async e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) { const u = await selfie.upload(f, "verification-selfie"); if (u) setSelfieUrl(u); } }} />
              {selfieUrl ? <img src={selfieUrl} alt="" style={{ height: "100%", width: "100%", objectFit: "cover" }} /> : (
                <><UiIcon name="camera" size={28} />{selfie.uploading ? t(lang, "uploading_ellipsis") : t(lang, "vfy_selfie_take")}</>
              )}
            </label>
            {!!selfieUrl && <div style={{ textAlign: "center", marginTop: 8, fontSize: 12.5, color: K.muted }}>{t(lang, "vfy_selfie_retake_hint")}</div>}
          </Card>

          <Card>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 16, fontWeight: 700 }}>{t(lang, "vfy_step_doc_title")}</span>
              <Chip>{t(lang, "vfy_optional")}</Chip>
            </div>
            <div style={{ fontSize: 12.5, color: K.muted, lineHeight: 1.45, marginBottom: 14 }}>{t(lang, "vfy_step_doc_desc")}</div>
            <DocPicker lang={lang} docType={docType} setDocType={setDocType} docUrl={docUrl} setDocUrl={setDocUrl} />
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 14, fontSize: 12.5, color: K.muted, lineHeight: 1.45 }}>
              <UiIcon name="info" size={15} style={{ marginTop: 1 }} /><span>{t(lang, "vfy_doc_later")}</span>
            </div>
          </Card>

          <Field label={t(lang, "verify_identity_note_label")}>
            <textarea value={note} onChange={e => setNote(e.target.value.slice(0, 300))} placeholder={t(lang, "verify_note_placeholder")} style={{ ...fieldStyle, height: 80, padding: "12px 14px", resize: "none", fontFamily: "inherit" }} />
          </Field>

          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: K.muted, lineHeight: 1.5, marginBottom: 16 }}>
            <UiIcon name="lock" size={15} style={{ marginTop: 1 }} /><span>{t(lang, "vfy_privacy")}</span>
          </div>

          <Button onClick={submit} disabled={!selfieUrl || submitting || selfie.uploading || (!!docUrl && !docType)}>
            {submitting ? t(lang, "sending_ellipsis") : t(lang, "verify_submit_button")}
          </Button>
        </>
      )}
      {trackerOpen && user?.id && <VerificationTracker userId={user.id} onClose={() => setTrackerOpen(false)} />}
    </Screen>
  );
}
